import type { User } from "firebase/auth"
import { create } from "zustand"
import { persist } from "zustand/middleware"

import type { SessionProfile, TenantAccess } from "@/lib/api/types"
import { bindTenantSession } from "@/lib/pb/clients"
import { clearTenantApiUrl, setTenantApiUrl } from "@/lib/tenant"

/**
 * `loading` is the honest starting state: Firebase restores the session
 * asynchronously, so until it reports in we genuinely don't know. Treating that
 * moment as signed-out is what makes apps bounce people to the login screen on
 * every refresh.
 */
export type AuthStatus = "loading" | "authenticated" | "anonymous"

interface AuthState {
  status: AuthStatus
  user: User | null
  /** The business currently being worked in. */
  tenant: TenantAccess | null
  /** Who the user is inside that business, assembled once its session opens. */
  profile: SessionProfile | null
  setUser: (user: User | null) => void
  selectTenant: (tenant: TenantAccess) => void
  /** Takes in a changed role for the same business, keeping its session. */
  refreshTenant: (tenant: TenantAccess) => void
  /** Drops a business the account may no longer work in. */
  leaveTenant: () => void
  setProfile: (profile: SessionProfile | null) => void
  reset: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      status: "loading",
      user: null,
      tenant: null,
      profile: null,

      setUser: (user) =>
        set({ user, status: user ? "authenticated" : "anonymous" }),

      selectTenant: (tenant) => {
        // The PocketBase client reads this on construction, so it has to land
        // before any screen that depends on the selection renders.
        setTenantApiUrl(tenant.serverUrl)
        // This tab's requests carry this business's session from here on,
        // whatever another tab has open (MANIT-1163).
        bindTenantSession(tenant)
        set({ tenant, profile: null })
      },

      refreshTenant: (tenant) => {
        setTenantApiUrl(tenant.serverUrl)
        bindTenantSession(tenant)
        set({ tenant })
      },

      leaveTenant: () => {
        clearTenantApiUrl()
        bindTenantSession(null)
        set({ tenant: null, profile: null })
      },

      setProfile: (profile) => set({ profile }),

      reset: () => {
        clearTenantApiUrl()
        bindTenantSession(null)
        set({ status: "anonymous", user: null, tenant: null, profile: null })
      },
    }),
    {
      name: "manito.auth",
      // The Firebase user is restored by the SDK and the profile is rebuilt
      // from the tenant session, so persisting either would only create a
      // chance for them to go stale.
      partialize: (state) => ({ tenant: state.tenant }),
      onRehydrateStorage: () => (state) => {
        if (!state?.tenant) return
        setTenantApiUrl(state.tenant.serverUrl)
        bindTenantSession(state.tenant)
      },
    },
  ),
)
